// src/pages/Home.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { computeTopPercentile } from "../utils.js";
import "./Result.css";

function Home() {
  const [instagramId, setInstagramId] = useState("");
  const [followerCount, setFollowerCount] = useState("");
  const [category, setCategory] = useState("");

  // (A) participants만 구글 시트로 관리 (shares 제거)
  const [participants, setParticipants] = useState(0);

  // 로딩 (Get Results 용)
  const [loading, setLoading] = useState(false);

  // 토스트
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const navigate = useNavigate();

  // (B) 구글 스크립트 URL (participants 전용)
  const scriptUrl =
    "https://script.google.com/macros/s/AKfycbyjKhD-KvVOgO49ENjX5k5RNn5_yTL3NN1zNWd6o4lJ7WcLywHUGNNVlPJvP0TRT3C60g/exec";

  // (C) 페이지 로드시 participants 가져오기
  useEffect(() => {
    fetchParticipants();
  }, []);

  async function fetchParticipants() {
    try {
      const formData = new URLSearchParams();
      formData.append("action", "getCounters");

      const res = await fetch(scriptUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      const data = await res.json();

      // participants만 사용, shares는 무시
      if (!data.error && data.participants) {
        setParticipants(data.participants);
      }
    } catch (err) {
      console.error("fetchParticipants error:", err);
    }
  }

  // (D) 토스트 표시
  const showToast = (msg) => {
    setToastMessage(msg);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2000);
  };

  // (E) "Get Results" → participants +1
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const count = Number(followerCount);
    const recentCollabs = 20;
    const campaignCost = 30;

    // 인플루언서 저장 + participants +1 로직
    try {
      const formData = new URLSearchParams();
      // action 생략 시 doPost에서 participants+1
      formData.append("instagramId", instagramId);
      formData.append("followerCount", followerCount);
      formData.append("category", category);

      const response = await fetch(scriptUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      const result = await response.json();
      console.log("서버 응답:", result);

      if (result.error) {
        alert("서버 에러: " + result.error);
      } else {
        // participants 로컬값 +1
        setParticipants((prev) => prev + 1);

        // 다시 fetchParticipants
        await fetchParticipants();

        // percentile → high/low 이동
        const topPercentile = computeTopPercentile(count, category);
        const route = topPercentile <= 20 ? "/result/macro" : "/result/micro";
        navigate(route, {
          state: {
            instagramId,
            followerCount: count,
            category,
            recentCollabs,
            campaignCost,
          },
        });
      }
    } catch (err) {
      console.error("error:", err);
      alert("Oh no! An error has occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Copy Link → fallback if navigator.clipboard fails
  const handleCopyLink = async () => {
    const copyText = "https://beautyscouter.simpleseed.ai";
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(copyText);
      } else {
        // Fallback
        const tempInput = document.createElement("textarea");
        tempInput.value = copyText;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand("copy");
        document.body.removeChild(tempInput);
      }
      showToast("Link has been copied");
    } catch (err) {
      console.error("Copy Link error:", err);
      alert("An error occurred. Check console for details.");
    }
  };

  return (
    <div className="home-container">
      {loading && <div className="loading-overlay">Loading...</div>}
      {toastVisible && <div className="toast-message">{toastMessage}</div>}

      <img
        src="https://beauty-scouter.s3.us-east-2.amazonaws.com/main_logo.png"
        alt="Share Icon"
        className="title"
      />
      <h2 className="home-subtitle">What’s Your Beauty Score?</h2>

      <h3 className="home-title">Are you a beauty influencer?</h3>
      <p className="home-description">
        Find out your estimated earnings —
        <br />
        and see where you rank among the top creators!
      </p>

      {/* ====== 폼 시작 ====== */}
      <form className="home-form" onSubmit={handleSubmit}>
        {/* (1) Instagram Handle */}
        <div className="input-group handle-input">
          <img
            src="https://beauty-scouter.s3.us-east-2.amazonaws.com/IG.svg"
            alt="IG"
            className="input-icon"
          />
          <span className="fixed-at">@</span>
          <input
            type="text"
            className="handle-input-field"
            placeholder="Instagram handle"
            value={instagramId}
            onChange={(e) => setInstagramId(e.target.value)}
            required
            /* 
              ====== 커스텀 영어 메시지 ======
              (onInvalid, onInput)
            */
            onInvalid={(e) =>
              e.target.setCustomValidity("Please enter your Instagram handle.")
            }
            onInput={(e) => e.target.setCustomValidity("")}
          />
        </div>

        {/* (2) Follower Count */}
        <div className="input-group">
          <img
            src="https://beauty-scouter.s3.us-east-2.amazonaws.com/Follower.svg"
            alt="Follower"
            className="input-icon-2"
          />
          <input
            type="number"
            min="0"                  // ★ 음수 입력 막기
            className="handle-input-field-2"
            placeholder="Follower count"
            value={followerCount}
            onChange={(e) => setFollowerCount(e.target.value)}
            required
            onInvalid={(e) =>
              e.target.setCustomValidity("Please enter your follower count.")
            }
            onInput={(e) => e.target.setCustomValidity("")}
          />
        </div>

        {/* (3) Category */}
        <div className="input-group">
          <img
            src="https://beauty-scouter.s3.us-east-2.amazonaws.com/Category.svg"
            alt="Category"
            className="input-icon-3"
          />
          <select
            className="custom-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            required
            onInvalid={(e) =>
              e.target.setCustomValidity("Please choose your beauty category.")
            }
            onInput={(e) => e.target.setCustomValidity("")}
          >
            <option value="">Choose your beauty category</option>
            <option value="Makeup">Makeup</option>
            <option value="Skincare">Skincare</option>
            <option value="Haircare">Haircare</option>
            <option value="Body Care">Body Care</option>
            <option value="Nail Care">Nail Care</option>
            <option value="Perfume">Perfume</option>
            <option value="Men's care">Men's Care</option>
          </select>
        </div>

        <button type="submit" className="get-results-btn">
          Get Results
        </button>
      </form>
      {/* ====== 폼 끝 ====== */}
      <div className="participants-info">
        <img
          src="https://beauty-scouter.s3.us-east-2.amazonaws.com/user.svg"
          alt="user"
          className="etc-icon"
        />
        Participants | {participants}
      </div>

      {/* Share section (no share count, just link copy) */}
      <div className="share-section-home" style={{ marginTop: "1rem" }}>
        <p style={{ fontSize: "0.95rem", marginBottom: "0rem" }}>
          Share your results
        </p>
      </div>

      <button className="copy-link-btn" onClick={handleCopyLink}>
        <img
          src="https://beauty-scouter.s3.us-east-2.amazonaws.com/copy.svg"
          alt="copy"
          className="etc-icon-2"
        />
        Copy Link
      </button>
    </div>
  );
}

export default Home;